import { NotificationEvent, Notification } from "@/components/notificationsList";
import { CodicentAppState, MessageContent } from "codicent-app-sdk";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useRenderMonitor } from "./useRenderTester";
import { Project } from "@/models/project";
import { Teammate } from "@/models/user";
import { ParsedChatReply } from "@/models/chat";
import { mergePartialProject } from "@/models/validation";
import { debugTest } from "../debugTest";
import { ensureAllStageIdsInProject } from "@/utils/stageIdGenerator";

export interface ReqtangleCodicentProps {
  projects: Project[];
  loadProject: (project: string) => Promise<Project | null>;
  saveProject: (project: Project) => Promise<void>;
  chatWithCodicent: (message: string, messageId?: string) => Promise<any>;
  getFiles: (project: Project) => Promise<any[]>;
  uploadFile: (project: Project, filename: string, formData: FormData) => Promise<string>;
  deleteFile: (project: Project, fileId: string) => Promise<void>;
  parseChatReply: (reply: string) => ParsedChatReply | null;
  logEvent: (project: Project, id: string, link: string | undefined, event: NotificationEvent) => Promise<void>;
  getProjectMembers: (project: Project) => Promise<Teammate[]>;
  createChatPrompt: (project: Project, userNavigationPath: (string | number)[], question: string) => string;
  applyImprovements: (project: Project, parsedChatReply: ParsedChatReply) => Project;
  sendBugReport: (project: Project, messageId: string, userComments: string) => Promise<void>;
  getNotifications: () => Promise<Notification[]>;
  getProjects: () => Promise<Project[]>;
  loadingProjects: boolean;
  isInitialLoad: boolean;
  // voice: RealtimeVoice;
  createProject: (name: string, userEmail?: string) => Promise<{ codicent: string; token: string }>;
}

const baseUrl = "https://codicent.com/";

// Function to apply partial data at a specific path
const applyPartialAtPath = (project: Project, partialData: any, path: string): Project => {
  console.log(`Applying partial data at path: ${path}`);

  // Deep clone to avoid mutating the original
  const result = JSON.parse(JSON.stringify(project));

  // Parse the path to handle both simple keys and selectors like "submenus[id=functionalRequirements]"
  // Example: "workspaces.submenus[id=functionalRequirements].tabs[id=2].data.processes[id=2].stages"
  const pathParts: Array<{ type: "key" | "selector" | "index"; key: string; id?: string; index?: number }> = [];

  // Split by dots, but keep selectors intact
  const segments = path.split(".");
  for (const segment of segments) {
    const selectorMatch = segment.match(/^(\w+)\[id=(.+)\]$/);
    const indexMatch = segment.match(/^(\w+)\[(\d+)\]$/);

    if (selectorMatch) {
      // This is a selector like "submenus[id=functionalRequirements]"
      pathParts.push({ type: "selector", key: selectorMatch[1], id: selectorMatch[2] });
    } else if (indexMatch) {
      // This is an array index like "tabs[0]"
      pathParts.push({ type: "index", key: indexMatch[1], index: parseInt(indexMatch[2], 10) });
    } else {
      // This is a simple key
      pathParts.push({ type: "key", key: segment });
    }
  }

  // Navigate to the parent object
  let current: any = result;
  for (let i = 0; i < pathParts.length - 1; i++) {
    const part = pathParts[i];

    if (part.type === "selector") {
      // Navigate into the array and find by ID
      if (typeof current !== "object" || current === null) {
        console.warn(`Expected object at path segment ${i}, found:`, typeof current);
        return project;
      }

      const array = current[part.key];
      if (!Array.isArray(array)) {
        console.warn(`Expected array at ${part.key}, found:`, typeof array);
        return project;
      }

      const found = array.find((item: any) => String(item?.id) === String(part.id));
      if (!found) {
        console.warn(`Could not find item with id=${part.id} in ${part.key}`);
        return project;
      }

      current = found;
    } else if (part.type === "index") {
      // Navigate into the array at specific index
      if (typeof current !== "object" || current === null) {
        console.warn(`Expected object at path segment ${i}, found:`, typeof current);
        return project;
      }

      const array = current[part.key];
      if (!Array.isArray(array)) {
        console.warn(`Expected array at ${part.key}, found:`, typeof array);
        return project;
      }

      if (part.index === undefined || part.index < 0 || part.index >= array.length) {
        console.warn(`Array index ${part.index} out of bounds for ${part.key} (length: ${array.length})`);
        return project;
      }

      current = array[part.index];
    } else {
      // Simple key navigation
      if (typeof current !== "object" || current === null) {
        console.warn(`Expected object at path segment ${i}, found:`, typeof current);
        return project;
      }

      if (!(part.key in current)) {
        current[part.key] = {}; // Create missing objects
      }
      current = current[part.key];
    }
  }

  // Handle the final part
  const finalPart = pathParts[pathParts.length - 1];

  if (finalPart.type === "selector") {
    // Final part is a selector - we need to update items in an array by ID
    const array = current[finalPart.key];
    if (!Array.isArray(array)) {
      console.warn(`Expected array at final ${finalPart.key}, found:`, typeof array);
      return project;
    }

    // If partialData is an array, merge each item by ID
    if (Array.isArray(partialData)) {
      for (const newItem of partialData) {
        const idx = array.findIndex((item: any) => String(item?.id) === String(newItem?.id));
        if (idx !== -1) {
          // Merge with existing item
          array[idx] = { ...array[idx], ...newItem };
        } else {
          // Add new item
          array.push(newItem);
        }
      }
    } else {
      // partialData is a single object - find and update by ID
      const idx = array.findIndex((item: any) => String(item?.id) === String(finalPart.id));
      if (idx !== -1) {
        array[idx] = { ...array[idx], ...partialData };
      } else {
        console.warn(`Could not find item with id=${finalPart.id} in ${finalPart.key}`);
      }
    }
  } else if (finalPart.type === "index") {
    // Final part is an array index - we need to update the item at that index
    const array = current[finalPart.key];
    if (!Array.isArray(array)) {
      console.warn(`Expected array at final ${finalPart.key}, found:`, typeof array);
      return project;
    }

    if (finalPart.index === undefined || finalPart.index < 0 || finalPart.index >= array.length) {
      console.warn(`Array index ${finalPart.index} out of bounds for ${finalPart.key} (length: ${array.length})`);
      return project;
    }

    // Update the item at the specified index
    // For objects, merge the partialData with existing data
    if (
      typeof partialData === "object" &&
      partialData !== null &&
      !Array.isArray(partialData) &&
      typeof array[finalPart.index] === "object" &&
      array[finalPart.index] !== null &&
      !Array.isArray(array[finalPart.index])
    ) {
      array[finalPart.index] = { ...array[finalPart.index], ...partialData };
    } else {
      // For non-objects or when array element doesn't exist, replace
      array[finalPart.index] = partialData;
    }
  } else {
    // Final part is a simple key
    if (typeof current !== "object" || current === null) {
      console.warn(`Expected object for final path segment, found:`, typeof current);
      return project;
    }

    // Check if partialData is an object with a single key that matches the final part
    if (
      typeof partialData === "object" &&
      partialData !== null &&
      !Array.isArray(partialData) &&
      Object.keys(partialData).length === 1 &&
      Object.keys(partialData)[0] === finalPart.key
    ) {
      // Extract the value from the wrapper object
      const actualValue = partialData[finalPart.key];
      console.log(`Unwrapping single-key object for path ${finalPart.key}:`, actualValue);
      current[finalPart.key] = actualValue;
    } else if (
      // For objects, merge the partialData with existing data
      typeof partialData === "object" &&
      partialData !== null &&
      !Array.isArray(partialData) &&
      typeof current[finalPart.key] === "object" &&
      current[finalPart.key] !== null &&
      !Array.isArray(current[finalPart.key])
    ) {
      // Merge objects - keep existing properties and add/update with partialData
      current[finalPart.key] = { ...current[finalPart.key], ...partialData };
    } else {
      // For non-objects or when current value doesn't exist, replace
      current[finalPart.key] = partialData;
    }
  }

  return result;
};

// Function to apply improvements to the project data
const applyImprovements = (
  project: Project,
  updatedProject: Project | Partial<Project> | any,
  isPartialUpdate = false,
  partialPath?: string
): Project => {
  console.log(`Applying ${isPartialUpdate ? "partial" : "complete"} improvements to project`);

  let finalProject: Project;

  if (isPartialUpdate) {
    if (partialPath) {
      // Apply partial data at specific path
      finalProject = applyPartialAtPath(project, updatedProject, partialPath);
      console.log("Applied partial update at specific path:", partialPath);
    } else {
      // Traditional partial merge
      finalProject = mergePartialProject(project, updatedProject as Partial<Project>);
      console.log("Merged partial update into existing project");
    }
  } else {
    finalProject = updatedProject as Project;
  }

  // Ensure all stages have valid IDs after applying improvements
  finalProject = ensureAllStageIdsInProject(finalProject);

  // Generate a simple structural diff between the original project and the updated project
  const diffs: { path: string; before: any; after: any }[] = [];

  const safeStringify = (v: any) => {
    try {
      return JSON.stringify(v);
    } catch {
      try {
        return String(v);
      } catch {
        return "[unserializable]";
      }
    }
  };

  const walk = (orig: any, upd: any, path = "") => {
    // identical reference or primitive equality
    if (orig === upd) return;

    // handle null/undefined differences
    if (orig == null || upd == null) {
      if (orig !== upd) diffs.push({ path: path || "root", before: orig, after: upd });
      return;
    }

    // different types
    if (typeof orig !== typeof upd) {
      diffs.push({ path: path || "root", before: orig, after: upd });
      return;
    }

    // arrays
    if (Array.isArray(orig) && Array.isArray(upd)) {
      const len = Math.max(orig.length, upd.length);
      for (let i = 0; i < len; i++) {
        walk(orig[i], upd[i], `${path || "root"}[${i}]`);
      }
      return;
    }

    // objects
    if (typeof orig === "object" && typeof upd === "object") {
      const keys = new Set([...Object.keys(orig), ...Object.keys(upd)]);
      for (const key of keys) {
        walk(orig[key], upd[key], path ? `${path}.${key}` : key);
      }
      return;
    }

    // primitives (string/number/boolean) that differ
    if (orig !== upd) {
      diffs.push({ path: path || "root", before: orig, after: upd });
    }
  };

  try {
    walk(project as any, finalProject as any);
    if (diffs.length === 0) {
      console.log("No changes detected between project and updatedProject.");
    } else {
      console.groupCollapsed(`Project changes (${diffs.length})`);
      // Show a compact table with path and before/after serialized
      console.table(
        diffs.map((d) => ({
          path: d.path,
          before: safeStringify(d.before),
          after: safeStringify(d.after),
        }))
      );
      // Also provide raw diffs for deeper inspection
      console.debug("Raw diffs:", diffs);
      console.groupEnd();
    }
  } catch (err) {
    console.error("Failed to compute project diff:", err);
  }

  return finalProject;
};

// Format navigation path to easier format to read for codicent
const formatNavigationPath = (pathArray: (string | number)[]): string => {
  return pathArray
    .map((item, index) => {
      const prev = pathArray[index - 1];

      // If the previous item is 'processes' and current is an ID → use [id=123] format
      if (prev === "processes" && typeof item === "string") {
        return `[id=${item}]`;
      }

      // If the current is a number → use brackets
      if (typeof item === "number") {
        return `[${item}]`;
      }

      // If previous is a string, and current is a string that looks like an ID/key → use dot
      if (typeof prev === "string") {
        return `.${isNaN(Number(item)) ? item : `["${item}"]`}`;
      }

      // First item → no dot
      if (index === 0) return item;

      // If previous is number or id → treat string as object key
      return `.${isNaN(Number(item)) ? item : `["${item}"]`}`;
    })
    .join("");
};

const createChatPrompt = (project: Project, userNavigationPath: (string | number)[], question: string): string => {
  const fullMessage = `I am at the following location in the project json:

**${formatNavigationPath(userNavigationPath)}**

This is the project json:

\`\`\`
${JSON.stringify(project)}
\`\`\`

**CRITICAL RESTRICTIONS:**

1. **ONLY modify the project JSON if you can make the EXACT changes requested by the user.**
2. **DO NOT claim to have made changes if you cannot modify the project JSON structure.**
3. **DO NOT create placeholders or example data - only provide actual, complete modifications.**
4. **If you cannot fulfill the request (e.g., adding a domain, creating new tabs, or modifying UI elements that require structural changes), you MUST respond with ONLY text explanation, WITHOUT providing the JSON response format below.**
5. **You can ONLY modify existing data within the project JSON structure. But you CAN add new tabs, domains, or major structural changes when needed.**
6. **If the requested change is impossible or outside your capabilities, respond with: "I cannot perform this action because [reason]. This requires manual changes to the project structure."**

Reply exactly with a json like this ONLY if you successfully modified the project JSON:
\`\`\`
{
"improvements": "[short user friendly description of enhancements done to the project json in a human readable text]",
"partial_project_json": [the updated project json, only the parts that changed, for partial updates],
"partial_path": "[the path to the part that changed, like workspaces.functionalRequirements.tabs[id=1].data]"
}
\`\`\`

---

**Instruction for partial_path and partial_project_json:**

When you return a response with \`partial_project_json\` and \`partial_path\`, the \`partial_path\` MUST ALWAYS point exactly to the part of the project JSON that is returned in \`partial_project_json\`. This means that if \`partial_path\` is, for example, \`workspaces.submenus[id=mysubmenu].tabs[3].data.processes[id=1].stages\`, then \`partial_project_json\` should be an array or object that can directly replace the value at that exact location in the project structure. Never return an object where the structure does not match exactly with the specified path.

If you change multiple different parts at different levels, split the response into several partial_path/partial_project_json pairs (or ask the user to specify which part should be updated). Always check that partial_path and partial_project_json are directly compatible.

---

Example of correct format:
\`\`\`json
{
  "partial_path": "workspaces.submenus[id=mysubmenu].tabs[id=1].data.processes[id=1].stages",
  "partial_project_json": [
    { "id": "stage1", "name": "..." },
    { "id": "stage2", "name": "..." }
  ]
}
\`\`\`

Example of incorrect format (should be avoided):
\`\`\`json
{
  "partial_path": "workspaces.submenus[id=mysubmenu].tabs[id=1].data.processes[id=1].stages",
  "partial_project_json": {
    "workspaces": { ... }
  }
}
\`\`\`

**Summary:**  
partial_path MUST ALWAYS point directly to the structure of partial_project_json - partial_project_json should be able to be written into that location in the project JSON without further transformation.

---

Where partial_project_json is the new improved/or enhanced project json part according to the following instruction:
\`\`\`
${question}
\`\`\`

**REMINDER: Only provide the JSON response format if you actually modified the project JSON. If you cannot fulfill the request, provide a clear explanation instead.**
`;

  return fullMessage;
};

// TODO: document, and add error handling
// createProject takes the user nickname (you), a project name (title) and the email of its owner and creates a new project
const createProject = async (
  nickname: string,
  name: string,
  email: string
): Promise<{ codicent: string; token: string }> => {
  const url = `${baseUrl}app/AppCreateCodicentAndNewUser?app=reqtangle&name=${encodeURIComponent(
    name
  )}&email=${encodeURIComponent(email)}`;
  const body = [
    { name: "AI_MODEL", value: "gpt-4.1", isSecret: false },
    {
      name: "CODICENT_FUNCTIONS_FILTER",
      value: "post_codicent_message,save_chat_conversation_title",
      isSecret: false,
    },
    { name: "AI_MAX_TOKENS", value: "155000", isSecret: false },
    { name: "CODICENT_CHAT_COLOR", value: "#f0e", isSecret: false },
    { name: "CODICENT_INDEX_TAGS", value: "webindex", isSecret: false },
    { name: "CO_ADMINS", value: "johan", isSecret: false },
    { name: "CODICENT_IS_PUBLIC", value: "1", isSecret: false },
    { name: "USE_FUNCTIONS", value: "1", isSecret: false },
    { name: "AI_USE_AZURE", value: "1", isSecret: false },
  ];

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status} - ${response.statusText}`);
  } else {
    const { codicent, token } = await response.json();
    const project = { description: "", mission: "", name, vision: "" };
    const message = `@${codicent} #reqtangle-project #data\n${JSON.stringify(project)}`;
    await postMessage({
      message,
      token,
    });
    // Enhanced project structure with comprehensive default data
    // Includes teammates, detailed project information, user roles, and all core sections
    // per functional requirements FR-012, FR-080-087
    const projectJson = {
      id: 1,
      codicent,
      name,
      workspaces: {
        id: "workspaces",
        name: "Workspaces",
        submenus: [
          {
            id: "functionalRequirements",
            name: "Functional requirements",
            tabs: [
              { id: "1", name: "Dashboard", type: "dashboard" },
              {
                id: "2",
                name: "Requirements 1",
                type: "flowchart",
                description: "",
                inScope: "",
                outOfScope: "",
                notes: "",
                data: {
                  processes: [
                    {
                      id: 1,
                      name: "Process 1",
                      stages: [
                        {
                          id: "1756816929784-stage1",
                          name: "Activity name",
                          taskGroups: [
                            {
                              tasks: [
                                {
                                  id: "1756816929784-1",
                                  name: "Step name",
                                  description: {
                                    who: "",
                                    what: "",
                                    why: "",
                                    gain: "",
                                  },
                                  progress: "Not Started",
                                  priority: "Nice to have",
                                  dueDate: "",
                                  assignee: "",
                                  businessRules: [],
                                  acceptanceCriteria: [],
                                },
                                {
                                  id: "1756816929784-2",
                                  name: "Step name",
                                  description: {
                                    who: "",
                                    what: "",
                                    why: "",
                                    gain: "",
                                  },
                                  progress: "Not Started",
                                  priority: "Nice to have",
                                  dueDate: "",
                                  assignee: "",
                                  businessRules: [],
                                  acceptanceCriteria: [],
                                },
                                {
                                  id: "1756816929784-3",
                                  name: "Step name",
                                  description: {
                                    who: "",
                                    what: "",
                                    why: "",
                                    gain: "",
                                  },
                                  progress: "Not Started",
                                  priority: "Nice to have",
                                  dueDate: "",
                                  assignee: "",
                                  businessRules: [],
                                  acceptanceCriteria: [],
                                },
                              ],
                            },
                          ],
                          progress: "Not Started",
                          priority: "Nice to have",
                          dueDate: "",
                          assignee: "",
                          description: {
                            who: "",
                            what: "",
                            why: "",
                            gain: "",
                          },
                          businessRules: [],
                          acceptanceCriteria: [],
                        },
                        {
                          id: "1756816929784-stage2",
                          name: "Activity name",
                          taskGroups: [],
                          progress: "Not Started",
                          priority: "Nice to have",
                          dueDate: "",
                          assignee: "",
                          description: {
                            who: "",
                            what: "",
                            why: "",
                            gain: "",
                          },
                          businessRules: [],
                          acceptanceCriteria: [],
                        },
                      ],
                    },
                  ],
                },
              },
            ],
          },
          {
            id: "nonFunctionalRequirements",
            name: "Non-Functional requirements",
            tabs: [
              {
                id: "1",
                name: "Availabillity",
                type: "table",
                data: {
                  type: "requirements",
                  description: "State the time periods during which the system must be available to users.",
                  data: [
                    {
                      id: "765534356",
                      title: "Availability of the search products",
                      description: "This is a brief description of availability of the search products",
                      acceptanceCriteria: [
                        "There must be a registration form with following fields: email, password, name, age, surname, phone",
                        "The search results should be sorted by relevance",
                        "The search results should be paginated",
                      ],
                    },
                    {
                      id: "765534358",
                      title: "Another title",
                      description: "This is a brief description of availability of the search products",
                      acceptanceCriteria: [
                        "There must be a registration form with following fields: email, password, name, age, surname, phone",
                        "The search results should be sorted by relevance",
                        "The search results should be paginated",
                      ],
                      notes: "This is a brief description of availability of the search products",
                    },
                    {
                      id: "965534359",
                      title: "Availability of the search products",
                      description: "This is a brief description of availability of the search products",
                      acceptanceCriteria: [
                        "There must be a registration form with following fields: email, password, name, age, surname, phone",
                        "The search results should be sorted by relevance",
                        "The search results should be paginated",
                      ],
                      notes: "This is a brief description of availability of the search products",
                    },
                    {
                      id: "765534361",
                      title: "Availability of the search products",
                      description: "This is a brief description of availability of the search products",
                      acceptanceCriteria: [
                        "There must be a registration form with following fields: email, password, name, age, surname, phone",
                        "The search results should be sorted by relevance",
                        "The search results should be paginated",
                      ],
                    },
                    {
                      id: "765534362",
                      title: "Availability of the search products",
                      description: "This is a brief description of availability of the search products",
                      acceptanceCriteria: [
                        "There must be a registration form with following fields: email, password, name, age, surname, phone",
                        "The search results should be sorted by relevance",
                        "The search results should be paginated",
                      ],
                      notes: "This is a brief description of availability of the search products",
                    },
                  ],
                },
              },
              {
                id: "2",
                name: "Compliance",
                type: "dashboard",
              },
              {
                id: "3",
                name: "Performance",
                type: "dashboard",
              },
              {
                id: "4",
                name: "Security",
                type: "dashboard",
              },
              {
                id: "5",
                name: "Scalabillity",
                type: "dashboard",
              },
              {
                id: "6",
                name: "Retention",
                type: "dashboard",
              },
              {
                id: "7",
                name: "Recovery",
                type: "dashboard",
              },
            ],
          },
          {
            id: "userRoles",
            name: "User roles",
            view: "flowchart",
            data: {
              userRoles: [
                {
                  id: "role-1",
                  name: "Project Manager",
                  description: "Responsible for project oversight, planning, and team coordination",
                  painPoints: "Managing timelines and resource allocation across multiple workstreams",
                  vision: "Deliver projects on time and within budget while maintaining team productivity",
                },
                {
                  id: "role-2",
                  name: "Developer",
                  description: "Builds and implements the technical solution",
                  painPoints: "Understanding complex requirements and managing technical debt",
                  vision: "Create clean, maintainable code that meets user needs effectively",
                },
                {
                  id: "role-3",
                  name: "Designer",
                  description: "Creates user experience and interface designs",
                  painPoints: "Balancing user needs with technical constraints and business goals",
                  vision: "Design intuitive interfaces that enhance user satisfaction and engagement",
                },
                {
                  id: "role-4",
                  name: "Stakeholder",
                  description: "Business representative with requirements ownership",
                  painPoints: "Communicating business needs clearly and managing changing priorities",
                  vision: "Ensure the solution addresses real business problems and delivers value",
                },
              ],
            },
          },
          {
            id: "stakeholders",
            name: "Stakeholders",
            view: "table",
            data: {
              type: "stakeholders",
              data: [
                {
                  id: "stakeholder-1",
                  name: "Sarah Johnson",
                  role: "role-1", // References Project Manager role
                },
                {
                  id: "stakeholder-2",
                  name: "Michael Chen",
                  role: "role-4", // References Stakeholder role
                },
                {
                  id: "stakeholder-3",
                  name: "Lisa Rodriguez",
                  role: "role-3", // References Designer role
                },
              ],
            },
          },
          {
            id: "glossary",
            name: "Glossary",
            view: "cards",
            data: [
              {
                id: 1,
                name: "API",
                description:
                  "Application Programming Interface - A set of protocols and tools for building software applications",
              },
              {
                id: 2,
                name: "User Story",
                description: "A short, simple description of a feature told from the perspective of the end user",
              },
              {
                id: 3,
                name: "Sprint",
                description: "A time-boxed iteration of a continuous development cycle in Agile methodology",
              },
            ],
          },
        ],
      },
      repository: {
        id: "repository",
        name: "Repository",
        view: "table",
        codicentData: true,
        data: [],
      },
      projectInformation: {
        id: "projectInformation",
        name: "Project Information",
        tabs: [
          {
            id: "1",
            name: "Overview",
            type: "simpleEdit",
            data: {
              type: "projectInformation",
              data: {
                vision:
                  "To deliver a comprehensive and user-friendly solution that addresses our stakeholders' core needs while maintaining high quality standards and fostering team collaboration.",
                mission:
                  "Create an innovative platform that streamlines workflows, enhances productivity, and provides measurable value to our users through intuitive design and robust functionality.",
                projectDescription:
                  "This project aims to develop a modern application that serves as the foundation for our organization's digital transformation. The solution will incorporate best practices in user experience design, scalable architecture, and agile development methodologies to ensure long-term success and maintainability.",
              },
            },
          },
        ],
      },
      teammates: [
        {
          id: "teammate-1",
          name: "Alex Thompson",
          nickname: "alexthompson",
          email: "alex.thompson@company.com",
        },
        {
          id: "teammate-2",
          name: "Maria Garcia",
          nickname: "mariagarcia",
          email: "maria.garcia@company.com",
        },
        {
          id: "teammate-3",
          name: "David Park",
          nickname: "davidpark",
          email: "david.park@company.com",
        },
      ],
    };

    await postMessage({
      message: `@${codicent} #reqtangle-project-json\n${JSON.stringify(projectJson)}`,
      token,
    });

    await fetch(
      `${baseUrl}app/AddFollower?nickname=${encodeURIComponent(codicent)}&followerNickname=${encodeURIComponent(
        nickname
      )}`,
      {
        method: "GET",
        headers: [
          ["Content-Type", "application/json; charset=utf-8"],
          ["Authorization", `Bearer ${token}`], // Use temp token (has admin rights)
        ],
      }
    );
    return { codicent, token };
  }
};

const generateProjectToken = async (project: Project) => {
  const expires = new Date();
  // add 1 day to the current date
  expires.setHours(expires.getHours() + 24);
  const response = await fetch(
    `${baseUrl}api/GenerateApiToken?project=${project.codicent}&expires=${expires.toISOString()}`,
    {
      method: "GET",
      headers: {
        authorization: "Bearer " + (window.Codicent as any).token,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const token = await response.text();
  console.log("Generated project token:", token);
  return token;
};

const postMessage = async ({
  message,
  parentId,
  type,
  token,
}: {
  message: string;
  parentId?: string;
  type?: string;
  token: string;
}): Promise<string> => {
  if (!message || message.trim() === "") {
    throw new Error("Parameter message is required to post a message");
  }

  const response = await fetch(`${baseUrl}app/AddChatMessage`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      id: undefined,
      content: message,
      type: type || "info",
      nickname: undefined,
      createdAt: new Date(),
      isNew: false,
      parentId: parentId || undefined,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status}`);
  }

  const result = await response.json();
  return result.id;
};

function parseChatReply(reply: string): ParsedChatReply | null {
  // Quick check for template/placeholder text to avoid unnecessary parsing
  if (
    reply.includes("[description of enhancements done to the project json in a human readable text]") ||
    reply.includes("[the updated project json]") ||
    reply.includes("\\`\\`\\`") || // Escaped backticks in templates
    reply.trim().length < 20
  ) {
    return null;
  }

  try {
    // Try to parse the entire reply as JSON first
    const parsed = JSON.parse(reply);

    // Validate required fields according to simplified interface
    if (!parsed.improvements || typeof parsed.improvements !== "string") {
      console.warn('Chat reply missing required "improvements" field');
      return null;
    }

    if (!parsed.partial_project_json || !parsed.partial_path) {
      console.warn('Chat reply missing required "partial_project_json" or "partial_path" fields');
      return null;
    }

    // Skip if improvements contains template text
    if (
      parsed.improvements.includes("[description of enhancements") ||
      parsed.improvements.includes("[the updated project") ||
      parsed.improvements.includes("\\`\\`\\`")
    ) {
      return null;
    }

    // Return the simplified result
    return {
      improvements: parsed.improvements,
      partial_project_json: parsed.partial_project_json,
      partial_path: parsed.partial_path,
    };
  } catch (error) {
    // Try to extract JSON from code blocks if direct parsing fails
    const codeBlockMatch = reply.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (codeBlockMatch) {
      try {
        const extracted = codeBlockMatch[1].trim();
        return parseChatReply(extracted);
      } catch (innerError) {
        console.warn("Failed to parse JSON from code block:", innerError);
      }
    }

    console.error("Failed to parse chat reply as JSON:", error);
    return null;
  }
}

const useReqtangleCodicent = (state: CodicentAppState) => {
  const stats = useRenderMonitor("useReqtangleCodicent");
  console.log(`🔄 useReqtangleCodicent render #${stats.renderCount} at ${new Date().toLocaleTimeString()}`);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // This is added for debugging purposes - you can remove it later
  useEffect(() => {
    debugTest();
  }, []);
  // Add this debugging to check if state object is changing
  const prevState = useRef<CodicentAppState>();
  useEffect(() => {
    if (prevState.current && prevState.current !== state) {
      // Only log every 5th time to reduce noise
      if (stats.renderCount % 5 === 0) {
        console.log("🚨 FOUND THE PROBLEM: CodicentAppState object reference changed!");
      }
    }
    prevState.current = state;
  });
  const { service, currentStateName, nickname } = state;
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // const setVoiceInstructions = useCallback(
  //   (instructions: string) => {
  //     if (voice) {
  //       voice.updateInstructions(instructions);
  //     }
  //   },
  //   [voice]
  // );

  // const setProjectVoiceInstructions = (project: Project) => {
  //   setVoiceInstructions(
  //     `You are the ReqTangle AI voice assistant. You are currently working on the project: ${
  //       project.name
  //     }. Help me with short answers about all this project data:\n${"```\n"}${JSON.stringify(
  //       project,
  //       null,
  //       2
  //     )}${"```\n"}`
  //   );
  // };

  // useEffect(() => {
  //   setVoiceInstructions(
  //     "You are the ReqTangle AI voice assistant. You answer questions about requirements, projects, and tasks. Keep answers short and not too polite, you are a college, educator and co-worker."
  //   );
  // }, [voice]);

  const memoizedService = useMemo(() => service, [service?.constructor?.name]); // Only change if service type changes

  const getProjectMembers = useCallback(
    async (project: Project) => {
      const projectInfo = await memoizedService.getProjectInfo(project.codicent);
      const followers = projectInfo.followers.map((f) => f.followingUser);
      const userInfos = await Promise.all(followers.map((f) => memoizedService.getUserInfo(f)));
      const members = userInfos.map((user) => ({
        id: user.nickname,
        nickname: user.nickname,
        email: user.email,
        name: user.name || user.email || user.nickname,
      }));
      return members;
    },
    [memoizedService]
  );

  const loadProject = useCallback(
    async (project: string) => {
      if (!window.Codicent) {
        console.error("window.Codicent is not available");
        return null;
      }
      try {
        const projects = await window.Codicent.getMessages({
          search: `@${project} #reqtangle-project-json`,
          length: 1,
        });
        if (projects.length === 0) {
          console.warn(`No project data found for: ${project}`);
          return null;
        }
        const json = new MessageContent(projects[0].content).content;
        let projectObj = JSON.parse(json) as Project;
        projectObj.teammates = await getProjectMembers(projectObj);

        // Ensure all stages have valid IDs
        projectObj = ensureAllStageIdsInProject(projectObj);

        // setProjectVoiceInstructions(projectObj);
        return projectObj;
      } catch (error) {
        console.error("Error fetching project:", error);
        return null;
      }
    },
    [getProjectMembers]
  );

  const getProjects = useCallback(async () => {
    if (!window.Codicent) {
      console.error("window.Codicent is not available");
      return [];
    }
    try {
      setLoadingProjects(true);
      const messages = await window.Codicent.getMessages({
        search: `#reqtangle-project`,
        length: 500,
      });
      const projects = messages
        .map((msg) => new MessageContent(msg.content))
        .filter((mc) => !mc.tags.includes("hidden"))
        .map((mc) => ({ id: mc.mentions[0], ...mc.data } as Project));
      // get only first (last) project within each mention
      const uniqueProjects: { [key: string]: Project } = {};
      projects.forEach((project) => {
        if (!uniqueProjects[project.name]) {
          uniqueProjects[project.name] = project;
        }
      });
      // Convert the object back to an array
      const projectsArray = Object.values(uniqueProjects);
      await Promise.all(
        projectsArray.map((project) => {
          return loadProject((project as any).id)
            .then((loadedProject) => {
              if (loadedProject) {
                (project as any).name = loadedProject.name || (project as any).name;
                if ((loadedProject as any)?.projectInformation?.tabs?.[0]?.data?.data?.projectDescription) {
                  (project as any).description = (
                    loadedProject as any
                  )?.projectInformation?.tabs?.[0]?.data?.data?.projectDescription;
                }
              }
            })
            .catch((error) => {
              console.error(`Error loading project details ${(project as any).id}:`, error);
            });
        })
      );

      setProjects(projectsArray);
      return projectsArray;
    } catch (error) {
      console.error("Error fetching projects:", error);
      return [];
    } finally {
      setLoadingProjects(false);
      setIsInitialLoad(false);
    }
  }, [loadProject]);

  const saveProject = useCallback(async (project: Project) => {
    if (false) {
      // test
      console.warn("Saving project is disabled in test mode");

      // // create test file to test uploadFile
      // const testFile = new Blob([JSON.stringify(project)], { type: "application/json" });
      // const formData = new FormData();
      // formData.append("file", testFile, "test-project.json");
      // const testFileId = await uploadFile(project, "test-project.json", formData);
      // console.log("Test file uploaded with ID:", testFileId);

      // // test getting files
      // const files = await getFiles(project);
      // console.log("Test files fetched:", files);

      // // test deleting a file
      // const files = await getFiles(project);
      // if (files.length > 0) {
      //   await deleteFile(project, files[0].id);
      //   console.log("Test file deleted successfully");
      //   const files2 = await getFiles(project);
      //   console.log("Files after deletion:", files2);
      // } else {
      //   console.warn("No files found to delete in test mode");
      // }

      // // test getting users
      // const projectInfo = await service.getProjectInfo(project.codicent);
      // const followers = projectInfo.followers.map((f) => f.followingUser);
      // // for each follower call service.getUserInfo(f.id), promise.all
      // const userInfos = await Promise.all(
      //   followers.map((f) => service.getUserInfo(f))
      // );

      // const response = userInfos.map((user) => ({
      //   nickname: user.nickname,
      //   email: user.email,
      //   name: user.name || user.email || user.nickname,
      // }));
      // console.log("Test users fetched:", response
      return;
    }

    try {
      const json = JSON.stringify(project);
      window.Codicent.postMessage({
        message: `@${project.codicent} #reqtangle-project-json\n${json}`,
        type: "info",
      });
      console.log("Project saved successfully");
      // setProjectVoiceInstructions(project);
    } catch (error) {
      console.error("Error saving project:", error);
    }
  }, []);

  useEffect(() => {
    if (currentStateName !== "hasAccess") return;
    getProjects();
  }, [currentStateName, getProjects]); // added service and getProjects to dependencies

  const chatWithCodicent = useCallback(
    async (message: string, messageId?: string) => {
      return await memoizedService.chat(message, messageId);
    },
    [memoizedService]
  );

  const getMessages = useCallback(
    async (props: {
      token: string;
      start?: number;
      length?: number;
      search?: string;
      afterTimestamp?: Date;
      beforeTimestamp?: Date;
      skipContent?: boolean;
    }) => {
      const { token, start, length, search, afterTimestamp, beforeTimestamp } = {
        ...{ start: 0, length: 10, search: "" },
        ...props,
      };
      const response = await fetch(
        `${baseUrl}app/GetChatMessages?start=${start}&length=${length}&search=${encodeURIComponent(search)}${afterTimestamp ? `&afterTimestamp=${afterTimestamp.toISOString()}` : ""
        }${beforeTimestamp ? `&beforeTimestamp=${beforeTimestamp.toISOString()}` : ""}${props.skipContent !== true ? "&includeContent=true" : ""
        }`,
        {
          method: "GET",
          headers: [
            ["Content-Type", "application/json; charset=utf-8"],
            ["Authorization", `Bearer ${token}`],
          ],
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const messages = await response.json();
      messages.forEach((m: any) => {
        m.createdAt = new Date(Date.parse(m.createdAt));
      });

      return messages as Message[];
    },
    []
  ); // No dependencies needed since it doesn't use any state/props

  const getFiles = useCallback(
    async (project: Project) => {
      if (!window.Codicent) {
        console.error("window.Codicent is not available");
        return [];
      }

      if (!project || !project.codicent) {
        console.warn("Project or project.codicent is not defined");
        return [];
      }

      const token = await generateProjectToken(project);
      if (!token) {
        console.error("Failed to generate project token");
        return [];
      }

      try {
        console.log("Fetching files for project:", project.codicent);
        const messages = await getMessages({
          search: `@${project.codicent} #file`,
          length: 500,
          token,
        });
        const fileIds = messages
          .map((msg) => ({ id: msg.id, mc: new MessageContent(msg.content) }))
          .filter((item) => item.mc.tags.includes("index"))
          .map((item) => ({
            id: item.id,
            fileId: item.mc.files[0],
            content: item.mc.content,
          }));
        const fileDetails = await Promise.all(
          fileIds.map(async ({ fileId, content, id }) => ({
            ...(await memoizedService.getFileInfo(fileId)),
            summary: content,
            id,
            fileId,
          }))
        );
        return fileDetails;
      } catch (error) {
        console.error("Error fetching files:", error);
        return [];
      }
    },
    [memoizedService, getMessages]
  );

  const deleteFile = useCallback(
    async (project: Project, fileId: string) => {
      const files = await getFiles(project);
      const file = files.find((f) => f.id === fileId);
      if (!file) {
        console.warn(`File with ID ${fileId} not found in project ${project.codicent}`);
        return;
      }
      const token = await generateProjectToken(project);
      await postMessage({
        message: `@${project.codicent} #file:${file.fileId} #noindex`,
        token,
        parentId: file.id,
      });
      console.log(`File with ID ${fileId} deleted from project ${project.codicent}`);
    },
    [getFiles]
  );

  const logEvent = useCallback(
    async (project: Project, id: string, link: string | undefined, event: NotificationEvent) => {
      if (!window.Codicent) {
        console.error("window.Codicent is not available");
        return;
      }

      const token = await generateProjectToken(project);
      if (!token) {
        console.error("Failed to generate project token");
        return;
      }

      try {
        await postMessage({
          message: `@${project.codicent} #event #target_id_${id}\n${JSON.stringify({
            ...event,
            target: { id, link },
          })}`,
          type: "info",
          token,
        });
        console.log("Event logged successfully");
      } catch (error) {
        console.error("Error logging event:", error);
      }
    },
    []
  );

  const uploadFile = useCallback(async (project: Project, filename: string, formData: FormData) => {
    const token = await generateProjectToken(project);
    if (!token) {
      throw new Error("Failed to generate project token");
    }

    const response = await fetch(`${baseUrl}app/UploadFile?filename=${encodeURIComponent(filename)}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const fileId = await response.json();
    // const messageId = await postMessage({
    //   message: `@${project.codicent} #file:${fileId} #index #summarize`,
    //   token,
    // });

    // return messageId;
    return fileId;
  }, []); // No dependencies needed

  const sendBugReport = useCallback(async (project: Project, messageId: string, userComments: string) => {
    const token = await generateProjectToken(project);
    if (!token) {
      throw new Error("Failed to generate project token");
    }
    try {
      await postMessage({
        message: `@${project.codicent} #bug \n${messageId}\n${userComments}\nhttps://codicent.com/chat/${project.codicent}/${messageId}`,
        type: "info",
        // type: 'bug_report',
        // parentId: msg.id,
        token,
      });
    } catch (error) {
      console.error("Error logging event:", error);
    }
  }, []);

  // Wrapper for createProject that uses current user's data
  const createUserProject = useCallback(
    async (projectName: string, userEmail?: string) => {
      // Try to get nickname and email from various sources
      let effectiveNickname = nickname;
      let effectiveEmail = userEmail;

      // If nickname is not available from appState, try to derive it from email
      if (!effectiveNickname && effectiveEmail) {
        // Extract nickname from email (part before @)
        effectiveNickname = effectiveEmail
          .split("@")[0]
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "");
      }

      // If email is not provided, try to construct one from nickname
      if (!effectiveEmail && effectiveNickname) {
        effectiveEmail = `${effectiveNickname}@example.com`;
      }

      // Final validation - we need at least a nickname or email to proceed
      if (!effectiveNickname || !effectiveEmail) {
        throw new Error(
          "Unable to create project: User information not available. Please ensure you are logged in and have a valid email address."
        );
      }

      return await createProject(effectiveNickname, projectName, effectiveEmail);
    },
    [nickname]
  );

  // Get notifications with fake data for now
  const getNotifications = useCallback(async (): Promise<Notification[]> => {
    const flow = await service.getPublicFlow("reqtangle");
    console.log("Fetched public flow messages for notifications:", flow);
    return flow.map((msg) => {
      const m = new MessageContent(msg.content);
      return {
        userId: "reqtangle",
        date: msg.createdAt.toISOString(),
        event: {
          object: m.content,
          action: "added",
          type: "project",
        },
      } as Notification;
    });
    // Simulate async fetch - TODO: implement real data fetching
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve([
          {
            userId: "user-123",
            date: "2025-06-27T11:20:00Z",
            event: {
              object: "Filtering",
              action: "modified",
              relation: "Filtering",
              type: "task",
            },
          },
          {
            userId: "user-123",
            date: "2025-06-27T10:15:00Z",
            event: {
              object: "Search products",
              action: "added",
              type: "stage",
            },
          },
          {
            userId: "user-456",
            date: "2025-06-26T15:00:00Z",
            event: {
              object: "Checkout flow",
              action: "deleted",
              type: "stage",
            },
          },
          {
            userId: "user-789",
            date: "2025-06-25T09:05:00Z",
            event: {
              object: "Shipping domain",
              action: "added",
              type: "domain",
            },
          },
          {
            userId: "user-123",
            date: "2025-06-25T13:47:00Z",
            event: {
              object: "Filter preset",
              action: "added",
              relation: "Search products",
              type: "task",
            },
          },
        ]);
      }, 800);
    });
  }, []);

  return useMemo(
    () => ({
      projects,
      loadProject,
      saveProject,
      chatWithCodicent,
      getFiles,
      uploadFile,
      deleteFile,
      parseChatReply,
      logEvent,
      getProjectMembers,
      createChatPrompt,
      applyImprovements: (project: Project, parsedChatReply: ParsedChatReply) => {
        // Apply partial project update using path-based approach
        return applyImprovements(project, parsedChatReply.partial_project_json, true, parsedChatReply.partial_path);
      },
      sendBugReport,
      createProject: createUserProject,
      getNotifications,
      getProjects,
      loadingProjects,
      isInitialLoad,
    }),
    [
      projects,
      loadProject,
      saveProject,
      chatWithCodicent,
      getFiles,
      uploadFile,
      deleteFile,
      logEvent,
      getProjectMembers,
      sendBugReport,
      createUserProject,
      getProjects,
      loadingProjects,
      isInitialLoad,
    ]
  ) as ReqtangleCodicentProps;
};

export default useReqtangleCodicent;
